<!--
  TestRenderer.vue
  -----------------
  Auteur       : Janfix - Jean-Philippe Rivière
  Projet       : WASPL – Plateforme d'évaluation numérique
  Description  : Composant principal de rendu d’un test pour les apprenants.
  Licence      : AGPL-3.0 pour la communauté des développeurs et usage non commercial
                Propriétaire pour les utilisations professionnelles et commerciales
  Version      : 1.0.0
  Date         : 2025

  📌 Composant Vue 3 utilisant Bootstrap, Tabulator, et un système de rendu dynamique d’interactions.
-->

<template>
  <div class="test-renderer">
    <!-- Barre de navigation supérieure -->
    <topnavbar
      :testData="props.testData"
      :answers="props.answers"
      :stop-timer="props.stopTimer"
      :currentPageIndex="props.currentPageIndex"
      @time-up="$emit('time-up')"
      @toggle-test-map="$emit('toggle-test-map')"
      @navigate-to-page="props.handleNavigateToPage" />

    <div class="row">
      <div class="col-12">
        <div class="container wrapper" :style="containerStyles">
          <!-- Carte du test affichée si activée -->
          <transition name="slide-down">
            <TestMap v-if="props.showTestMap" :publication="publicationStore?.publicationData"
              :testData="props.testData" :answers="props.answers" :currentPageIndex="props.currentPageIndex"
              @navigate-to-page="props.handleNavigateToPage" />
          </transition>

          <!-- Titre et description -->
           
          <h1 class="bigTitle" v-if="props.testData">{{ props.testData.title }}</h1>
          <hr>
          <div class="row">
            <div class="col">
              <p>{{ props.testData.Description }}</p>
            </div>
            <div v-if="!props.lastPageReached && !props.hideNavigation" class="col text-end" style="margin-right:30px">
              <b>{{ $t('Page') }} {{ props.currentPageIndex + 1 }}</b>
            </div>
          </div>

          <!-- Stimulus en haut si configuré -->
          <div v-if="shouldShowStimulus('top')" 
               class="stimulus-container stimulus-top" 
               :class="{ 'stimulus-sticky': currentPageParameters.stimulus?.sticky }"
               v-html="processedStimulusContent"></div>

          <!-- 🎛️ Contenu principal avec disposition en colonnes si configuré -->
          <div v-if="currentPageParameters.layout?.columnType === 'double' && currentPageParameters.stimulus?.visible && currentPageParameters.stimulus?.position === 'left'" class="page-double-column" :style="getPageStyles">
            <!-- Stimulus à gauche -->
            <div class="stimulus-column" :style="{ width: leftColumnWidth }">
              <div class="stimulus-container stimulus-left"
                   :class="{ 'stimulus-sticky': currentPageParameters.stimulus?.sticky }"
                   v-html="processedStimulusContent"></div>
            </div>
            <!-- Séparateur redimensionnable -->
            <div v-if="currentPageParameters.layout?.resizableSeparator"
                 class="column-separator"
                 @mousedown="startResize"
                 @touchstart="startResize">
              <div class="separator-handle">
                <i class="fas fa-grip-lines-vertical"></i>
              </div>
            </div>
            <!-- Items à droite dans une seule colonne -->
            <div class="items-column" :style="{ width: rightColumnWidth }">
              <div class="pageContainer" :class="getPageClasses">
                <template v-if="!props.lastPageReached && currentPage">
                  <div class="itemContainer" v-for="child in currentPage.children" :key="child.id"
                    :class="{ 'highlight-unanswered': unansweredHighlights.includes(child.id) }">
                    <component :is="prepareInteractionComponent(child).component"
                      v-bind="prepareInteractionComponent(child).props" @interaction-started="handleInteractionStarted" />
                  </div>
                </template>
              </div>
            </div>
          </div>

          <div v-else-if="currentPageParameters.layout?.columnType === 'double' && currentPageParameters.stimulus?.visible && currentPageParameters.stimulus?.position === 'right'" class="page-double-column" :style="getPageStyles">
            <!-- Items à gauche dans une seule colonne -->
            <div class="items-column" :style="{ width: leftColumnWidth }">
              <div class="pageContainer" :class="getPageClasses">
                <template v-if="!props.lastPageReached && currentPage">
                  <div class="itemContainer" v-for="child in currentPage.children" :key="child.id"
                    :class="{ 'highlight-unanswered': unansweredHighlights.includes(child.id) }">
                    <component :is="prepareInteractionComponent(child).component"
                      v-bind="prepareInteractionComponent(child).props" @interaction-started="handleInteractionStarted" />
                  </div>
                </template>
              </div>
            </div>
            <!-- Séparateur redimensionnable -->
            <div v-if="currentPageParameters.layout?.resizableSeparator"
                 class="column-separator"
                 @mousedown="startResize"
                 @touchstart="startResize">
              <div class="separator-handle">
                <i class="fas fa-grip-lines-vertical"></i>
              </div>
            </div>
            <!-- Stimulus à droite -->
            <div class="stimulus-column" :style="{ width: rightColumnWidth }">
              <div class="stimulus-container stimulus-right"
                   :class="{ 'stimulus-sticky': currentPageParameters.stimulus?.sticky }"
                   v-html="processedStimulusContent"></div>
            </div>
          </div>

          <!-- Mode colonne simple ou pas de stimulus en colonnes -->
          <div v-else class="pageContainer" :class="getPageClasses" :style="getPageStyles">
            <template v-if="!props.lastPageReached && currentPage">
              <div class="itemContainer" v-for="child in currentPage.children" :key="child.id"
                :class="{ 'highlight-unanswered': unansweredHighlights.includes(child.id) }">
                <component :is="prepareInteractionComponent(child).component"
                  v-bind="prepareInteractionComponent(child).props" @interaction-started="handleInteractionStarted" />
              </div>
            </template>

            <!-- Stimulus en bas si configuré -->
            <div v-if="shouldShowStimulus('bottom')" 
                 class="stimulus-container stimulus-bottom" 
                 :class="{ 'stimulus-sticky': currentPageParameters.stimulus?.sticky }"
                 v-html="processedStimulusContent"></div>

            <!-- Dernière page -->
            <div v-if="props.lastPageReached">
              <LastPage :testData="props.testData" />
            </div>
          </div>

          <!-- 🔧 Boutons de navigation TOUJOURS en dehors du système de colonnes -->
          <div v-if="!props.hideNavigation" class="container navigation-buttons mt-4 mb-4">
            <div class="row">
              <div class="col">
                <button v-if="showPrevButton" class="btn btn-primary me-2" @click="props.onPrevPage">
                  {{ $t('Previous page') }}
                </button>
              </div>
              <div class="col text-end">
                <button
                  v-if="!props.lastPageReached && currentPage && props.currentPageIndex + 1 < props.testData.pages.length"
                  class="btn btn-primary" @click="handleNextPageWithValidation" data-next="nextPage">
                  {{ $t('Next Page') }}
                </button>

                <button
                  v-if="!props.lastPageReached && currentPage && props.currentPageIndex + 1 === props.testData.pages.length"
                  class="btn btn-primary sendToServer" @click="handleSendResultsWithValidation" data-send="sendData">
                  {{ $t('Send Results') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pied de page -->
    <Footer />

    <!-- Notification Toast -->
    <div class="toast-container position-fixed bottom-0 end-0 p-3" style="z-index: 9999">
      <div class="toast align-items-center text-white bg-danger border-0" role="alert" aria-live="assertive"
        aria-atomic="true" ref="unansweredToast">
        <div class="d-flex">
          <div class="toast-body">
            {{ $t('You must answer all the questions before going on to the next page') }}
          </div>
          <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast"
            aria-label="Close"></button>
        </div>
      </div>
    </div>
  </div>
</template>


<script setup>


// 📦 Importation des dépendances
import { ref, computed, onMounted, watch } from 'vue';
import { Toast } from 'bootstrap';

import topnavbar from './topnavbar.vue';
import TestMap from './TestMap.vue';
import LastPage from './LastPage.vue';
import Footer from '../views/footer.vue';

import { createInteraction } from '../interactions/InteractionFactory';
import { usePublicationStore } from '@/stores/usePublicationStore';
import { useResponsesStore } from '@/stores/useResponsesStore';
const responsesStore = useResponsesStore();
const token = localStorage.getItem('token');
const decoded = token ? JSON.parse(atob(token.split('.')[1])) : null;
const userId = decoded?._id || decoded?.sub;

function isInteractionReadOnly(questionId) {
  if (typeof publicationStore.publicationData === 'undefined') { alert("DATABASE EXTRACTION DE READ ONLY FAUSSE!") }
  if (!publicationStore.publicationData?.readOnlyAnswer) return false;
  if (!props.lockedQuestions) return false;

  return props.lockedQuestions.has(questionId);
}

function prepareInteractionComponent(child) {
  const interaction = getInteractionComponent(child);
  return {
    ...interaction,
    props: {
      ...interaction.props,
      questionIndexMap: props.questionIndexMap
    }
  };
}

function handleInteractionStarted(questionId) {
  console.log("[TestRenderer] ✏️ handleInteractionStarted pour", questionId);

  const question = getQuestionById(questionId);
  console.log(`[TestRenderer] 🧹 Question retrouvée`, question);
  console.log(`[TestRenderer] 🧹 Question TYPE`, question.el_Type);
  if (!question) {
    console.warn(`[TestRenderer] ❗ Question introuvable pour ID ${questionId}`);
    return;
  }
}

// Ajoute une petite fonction utilitaire pour retrouver la question :
function getQuestionById(id) {
  return props.testData.elements.find(q => q.el_ID === id);
}

function getInteractionComponentPrepared(child) {
  const interactionData = getInteractionComponent(child);
  return interactionData;
}


function getInteractionComponent(child) {
  const question = getQuestion(child);
  const interaction = createInteraction(question, props.testData);

  const savedResponse = responsesStore.getResponseForQuestion(userId, question.el_ID);

  if (savedResponse) {
    const answerToRestore = savedResponse.response ?? savedResponse.selectedOptions ?? savedResponse.answered;
    interaction.restoreAnswer(answerToRestore);
  }

  // 🧠 Déterminer readOnly au niveau de l'interaction
  const questionReadOnly = isInteractionReadOnly(child.id);

  const interactionData = interaction.render();

  return {
    component: interactionData.component,
    props: {
      ...interactionData.props,
      questionIndexMap: props.questionIndexMap,
      readOnly: questionReadOnly, // 🔥 Ici on injecte readOnly proprement !
    }
  };
}






// 🧠 État local
const publicationStore = usePublicationStore();
const unansweredHighlights = ref([]);

// 🎯 Props attendues
const props = defineProps({
  testData: Object,
  questionIndexMap: Object,
  readOnly: Boolean,
  lockedQuestions: Object,
  showTestMap: Boolean,
  currentPageIndex: Number,
  lastPageReached: Boolean,
  answers: Object,
  stopTimer: Boolean,
  hideNavigation: { type: Boolean, default: false },
  onPrevPage: Function,
  onNextPage: Function,
  onSendResults: Function,
  handleNavigateToPage: Function
});

const emit = defineEmits(['time-up', 'toggle-test-map', 'lock-question']);

// 🧮 Page actuelle calculée
const currentPage = computed(() => {
  if (
    !props.testData ||
    !Array.isArray(props.testData.pages) ||
    props.testData.pages.length === 0 ||
    props.currentPageIndex === null ||
    props.currentPageIndex === undefined ||
    props.currentPageIndex >= props.testData.pages.length
  ) {
    return null;
  }
  return props.testData.pages[props.currentPageIndex];
});

const showPrevButton = computed(() => {
  const browsingMode = publicationStore.publicationData?.browsing || 'free';

  if (browsingMode === 'noBack') return false;
  if (props.currentPageIndex === 0) return false;
  if (props.lastPageReached) return false;

  return true;
});

// 🎛️ Paramètres effectifs de la page courante
const currentPageParameters = computed(() => {
  if (!currentPage.value?.effectiveParameters) {
    console.log('🎛️ Pas de paramètres pour la page courante, utilisation des défauts');
    return {
      layout: { columnType: 'single', singleColumnWidth: 80 },
      stimulus: { visible: false, content: '', position: 'top' },
      theme: { mode: 'light' }
    };
  }
  
  console.log('🎛️ Paramètres de la page courante:', currentPage.value.effectiveParameters);
  return currentPage.value.effectiveParameters;
});

// 🎛️ Classes CSS pour la mise en page
const getPageClasses = computed(() => {
  const params = currentPageParameters.value;
  const classes = ['page-with-parameters'];
  
  // Classes pour le layout
  if (params.layout?.columnType) {
    classes.push(`layout-${params.layout.columnType}`);
  }
  
  // Classes pour le thème
  if (params.theme?.mode) {
    classes.push(`theme-${params.theme.mode}`);
  }
  
  // Classe pour la largeur
  if (params.layout?.columnType === 'single') {
    classes.push(`width-${params.layout.singleColumnWidth || 80}`);
  }
  
  return classes.join(' ');
});

// 🎛️ Styles CSS dynamiques
// Computed for conditional test display settings
const containerStyles = computed(() => {
  const settings = props.testData?.displaySettings;
  if (!settings) return {};

  return {
    margin: `${settings.containerMargin || 20}px auto`,
    padding: `${settings.containerPadding || 20}px`,
    backgroundColor: settings.containerBackground || '#ffffff',
    maxWidth: settings.maxWidth && settings.maxWidth > 0 ? `${settings.maxWidth}px` : '100%',
    marginLeft: settings.containerAlignment === 'left' ? '0' :
                settings.containerAlignment === 'right' ? 'auto' : 'auto',
    marginRight: settings.containerAlignment === 'right' ? '0' :
                 settings.containerAlignment === 'left' ? 'auto' : 'auto',
    '--question-spacing': `${settings.questionSpacing || 15}px`
  };
});

const pageContainerStyles = computed(() => {
  const settings = props.testData?.displaySettings;
  if (!settings) return {};

  return {
    padding: `${settings.pagePadding || 15}px`,
    backgroundColor: settings.pageBackground || '#f8f9fa'
  };
});

const getPageStyles = computed(() => {
  const params = currentPageParameters.value;
  const settings = props.testData?.displaySettings;
  const styles = { ...pageContainerStyles.value };

  // Largeur pour colonne simple
  if (params.layout?.columnType === 'single') {
    styles['--content-width'] = `${params.layout.singleColumnWidth || 80}%`;
  }

  // Distribution des colonnes
  if (params.layout?.columnType === 'double') {
    const distribution = params.layout.contentDistribution || '50-50';
    const [left, right] = distribution.split('-');
    styles['--left-column'] = `${left}%`;
    styles['--right-column'] = `${right}%`;
  }

  return styles;
});

// 📏 Gestion du redimensionnement des colonnes
const isResizing = ref(false);
const leftColumnWidth = ref('calc(50% - 4px)');
const rightColumnWidth = ref('calc(50% - 4px)');

// Initialiser les largeurs des colonnes en fonction des paramètres
watch(currentPageParameters, (params) => {
  if (params.layout?.columnType === 'double' && !isResizing.value) {
    const distribution = params.layout.contentDistribution || '50-50';
    const [left, right] = distribution.split('-');

    // Si le séparateur est activé, soustraire 4px de chaque côté pour le séparateur de 8px
    if (params.layout?.resizableSeparator) {
      leftColumnWidth.value = `calc(${left}% - 4px)`;
      rightColumnWidth.value = `calc(${right}% - 4px)`;
    } else {
      leftColumnWidth.value = `${left}%`;
      rightColumnWidth.value = `${right}%`;
    }
  }
}, { immediate: true });

const startResize = (e) => {
  e.preventDefault();
  isResizing.value = true;

  const onMouseMove = (moveEvent) => {
    if (!isResizing.value) return;

    const container = moveEvent.target.closest('.page-double-column');
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const mouseX = moveEvent.clientX || moveEvent.touches?.[0]?.clientX;

    if (!mouseX) return;

    // Calculer le pourcentage
    const percentage = ((mouseX - containerRect.left) / containerRect.width) * 100;

    // Limiter entre 20% et 80% pour éviter des colonnes trop petites
    const clampedPercentage = Math.max(20, Math.min(80, percentage));

    // Soustraire 4px de chaque côté pour le séparateur de 8px
    leftColumnWidth.value = `calc(${clampedPercentage}% - 4px)`;
    rightColumnWidth.value = `calc(${100 - clampedPercentage}% - 4px)`;
  };

  const onMouseUp = () => {
    isResizing.value = false;
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
    document.removeEventListener('touchmove', onMouseMove);
    document.removeEventListener('touchend', onMouseUp);
  };

  document.addEventListener('mousemove', onMouseMove);
  document.addEventListener('mouseup', onMouseUp);
  document.addEventListener('touchmove', onMouseMove);
  document.addEventListener('touchend', onMouseUp);
};

// 🎛️ Détermine si le stimulus doit être affiché à une position donnée
const shouldShowStimulus = (position) => {
  const params = currentPageParameters.value;
  return params.stimulus?.visible && params.stimulus?.position === position && params.stimulus?.content;
};

// 🎬 Convertit le contenu du stimulus en remplaçant les placeholders vidéo par de vraies vidéos
const processedStimulusContent = computed(() => {
  const params = currentPageParameters.value;
  if (!params.stimulus?.content) return '';
  
  let content = params.stimulus.content;
  
  // Remplacer les placeholders vidéo par de vraies vidéos
  content = content.replace(
    /<p data-video-url="([^"]*)" data-video-size="([^"]*)"[^>]*>📹 VIDÉO:[^<]*<\/p>/g,
    (match, videoUrl, videoSize) => {
      console.log('🎬 Conversion placeholder vers vidéo:', videoUrl, videoSize);
      
      // Créer le HTML de la vidéo avec les mêmes paramètres optimisés
      const sizeClass = videoSize === 'full' ? '' : `video-${videoSize}`;
      return `<div class="video-container ${sizeClass}"><iframe 
        src="${videoUrl}" 
        frameborder="0" 
        allowfullscreen 
        allow="picture-in-picture" 
        style="width:100%;height:300px;border-radius:8px;"
        loading="lazy"
        referrerpolicy="strict-origin-when-cross-origin"
      ></iframe></div>`;
    }
  );
  
  return content;
});

// 🔎 Récupération des données de question et composant associé
function getQuestion(child) {
  return props.testData.elements.find(el => el.el_ID === child.id);
}

// ⚠️ Gestion des questions non répondues
function triggerHighlight(id) {
  const index = unansweredHighlights.value.indexOf(id);
  if (index !== -1) unansweredHighlights.value.splice(index, 1);

  setTimeout(() => {
    unansweredHighlights.value.push(id);
    setTimeout(() => {
      const idx = unansweredHighlights.value.indexOf(id);
      if (idx !== -1) unansweredHighlights.value.splice(idx, 1);
    }, 2500);
  }, 0);
}

function hasUnansweredQuestion() {
  console.log("NOSKIPPER")
  const skipAllowed = publicationStore?.publicationData?.skip;
  if (skipAllowed) return false;

  const page = currentPage.value;
  if (!page || !Array.isArray(page.children)) return false;

  let foundUnanswered = false;

  for (const child of page.children) {
    const question = getQuestionById(child.id);
    console.log(question)
    // ⛔ On ignore les interactions de type 'Message'

    if (question?.el_Type === 'message') continue;

    const answer = props.answers[child.id];
    const isUnanswered =
      answer === undefined ||
      answer === null ||
      (Array.isArray(answer) && answer.length === 0) ||
      (typeof answer === 'string' && answer.trim() === '');

    if (isUnanswered) {
      triggerHighlight(child.id);
      foundUnanswered = true;
    }
  }

  return foundUnanswered;
}


// ▶️ Navigation conditionnelle
function handleNextPageWithValidation() {
  if (hasUnansweredQuestion()) {
    if (toastInstance) toastInstance.show();
    return;
  }

  // 🚀 🔥 On émet 'lock-hotspot' pour chaque élément de la page avant de changer de page
  const page = currentPage.value;
  if (page && page.children) {
    for (const child of page.children) {
      const question = getQuestionById(child.id);
      if (question?.el_Type === 'hotSpot') {
        window.dispatchEvent(new CustomEvent('lock-hotspot', { detail: { questionId: child.id } }));
      }
      if (question?.el_Type === 'makingPairs') {
        window.dispatchEvent(new CustomEvent('lock-makingPairs', { detail: { questionId: child.id } }));
}




    }
  }

  props.onNextPage();
}


// Fonction pour vérifier si on est en mode agent
const isAgentMode = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return false;
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.agent === true;
  } catch {
    return false;
  }
};

// Fonction pour attendre que tous les ShortAnswer soient complétés (SEULEMENT EN MODE AGENT)
const waitForAllShortAnswersCompletion = async (maxWaitTime = 30000) => {
  // Si on n'est pas en mode agent, pas d'attente
  if (!isAgentMode()) {
    console.log('👤 [TESTRENDERER] Not in agent mode, skipping ShortAnswer wait');
    return Promise.resolve(true);
  }
  
  return new Promise((resolve) => {
    console.log('🤖 [TESTRENDERER] Agent mode detected - checking for pending ShortAnswer completions...');
    
    const startTime = Date.now();
    const checkInterval = 500; // Vérifier toutes les 500ms
    
    const checkCompletion = () => {
      // Vérifier s'il y a des ShortAnswer en cours de génération
      const pendingShortAnswers = document.querySelectorAll('textarea[data-shortanswer-generating="true"]');
      
      console.log(`🔍 [TESTRENDERER] Checking: ${pendingShortAnswers.length} textarea(s) with generating attribute`);
      console.log('📊 [TESTRENDERER] Current store state:', responsesStore.$state.responses.length, 'responses');
      console.log('📋 [TESTRENDERER] Store details:', responsesStore.$state.responses.map(r => ({ type: r.type, questionId: r.questionId, answered: r.answered })));
      
      if (pendingShortAnswers.length === 0) {
        console.log('✅ [TESTRENDERER] All ShortAnswer interactions completed');
        resolve(true);
      } else if (Date.now() - startTime > maxWaitTime) {
        console.log(`⏰ [TESTRENDERER] Timeout waiting for ShortAnswer completions (${pendingShortAnswers.length} still pending)`);
        resolve(false);
      } else {
        console.log(`⏳ [TESTRENDERER] Waiting for ${pendingShortAnswers.length} ShortAnswer completions...`);
        setTimeout(checkCompletion, checkInterval);
      }
    };
    
    checkCompletion();
  });
};

async function handleSendResultsWithValidation() {
  if (hasUnansweredQuestion()) {
    if (toastInstance) toastInstance.show();
    return;
  }
  
  // Attendre que tous les ShortAnswer soient complétés avant d'envoyer
  console.log('⏳ [TESTRENDERER] Waiting for all ShortAnswer completions before sending results...');
  await waitForAllShortAnswersCompletion();
  
  props.onSendResults();
}

// 📢 Toast Bootstrap
const unansweredToast = ref(null);
let toastInstance = null;

onMounted(() => {
  if (unansweredToast.value) {
    toastInstance = new Toast(unansweredToast.value);
  }
});
</script>

<style scoped>
.test-renderer {
  display: flex;
  flex-direction: column;
 
}

.wrapper {
  flex: 1;
  margin-top: 70px;
}

.sendToServer {
  margin-left: 10px;
}

/* Transitions pour l’affichage de la carte */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: transform 0.4s ease-in-out, opacity 0.4s ease-in-out;
}

.slide-down-enter-from,
.slide-down-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}

.slide-down-enter-to,
.slide-down-leave-from {
  transform: translateY(0);
  opacity: 1;
}

/* Boutons de navigation */
.navigation-buttons {
  padding: 0 50px 30px;
  position: relative;
  z-index: 1;
  width: 100%;
  clear: both;
  margin-top: 2rem !important;
}

/* Mise en surbrillance des questions non répondues */
.highlight-unanswered {
  border: 6px solid red;
  border-radius: 8px;
  animation: pulse-border 2.5s ease-out forwards;
}

@keyframes pulse-border {
  0% {
    border-color: red;
  }

  100% {
    border-color: transparent;
  }
}

/* 🎛️ Styles pour les paramètres de page (copiés de TestPreviewer.vue) */
.page-with-parameters {
  margin: 0 auto;
  transition: all 0.3s ease;
  margin-bottom: 3rem; /* Espace pour les boutons de navigation */
}

/* Largeurs pour colonne simple */
.page-with-parameters.layout-single.width-60 {
  max-width: var(--content-width, 60%);
}

.page-with-parameters.layout-single.width-70 {
  max-width: var(--content-width, 70%);
}

.page-with-parameters.layout-single.width-80 {
  max-width: var(--content-width, 80%);
}

.page-with-parameters.layout-single.width-100 {
  max-width: var(--content-width, 100%);
}

/* Thèmes */
.page-with-parameters.theme-dark {
  background-color: #2c3e50;
  color: #ecf0f1;
  border-radius: 8px;
  padding: 20px;
}

.page-with-parameters.theme-light {
  background-color: #ffffff;
  color: #2c3e50;
}

/* Stimulus containers */
.stimulus-container {
  margin: 20px 0;
  padding: 15px;
  background-color: #f8f9fa;
  border-left: 4px solid #007bff;
  border-radius: 4px;
}

/* Stimulus dans les colonnes : doit scroller si trop haut */
.stimulus-column .stimulus-container {
  margin: 0;
  max-height: none; /* Pas de limite interne, c'est la colonne qui limite */
}

.stimulus-top {
  margin-bottom: 30px;
}

.stimulus-bottom {
  margin-top: 30px;
}

.theme-dark .stimulus-container {
  background-color: #34495e;
  border-left-color: #3498db;
  color: #ecf0f1;
}

/* 🎛️ Support pour colonnes doubles : seul le stimulus utilise les colonnes */
.page-double-column {
  display: flex;
  gap: 0;
  align-items: flex-start;
  margin-bottom: 3rem; /* Espace pour les boutons de navigation */
  position: relative;
}

/* Les colonnes dans la disposition double */
.stimulus-column {
  /* Colonne qui contient le stimulus - elle s'adapte à la hauteur des items */
  padding: 0 15px;
  flex-shrink: 0;
  position: sticky;
  top: 80px; /* Position en dessous de la navbar */
  align-self: flex-start;
  max-height: calc(100vh - 100px); /* Hauteur maximale = hauteur viewport - marges */
  overflow-y: auto; /* Permettre le scroll si le contenu est trop haut */
}

.items-column {
  /* Colonne qui contient les items - définit la hauteur du conteneur */
  padding: 0 15px;
  flex-shrink: 0;
  flex-grow: 1;
}

.items-column .pageContainer {
  /* S'assurer que les items restent dans une seule colonne */
  display: block;
}

/* 📏 Séparateur redimensionnable entre les colonnes */
.column-separator {
  width: 8px;
  background: linear-gradient(to right, transparent, #d0d0d0 50%, transparent);
  border-left: 1px solid #c0c0c0;
  border-right: 1px solid #c0c0c0;
  cursor: col-resize;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding-top: 20px;
  transition: all 0.3s ease;
  user-select: none;
  position: sticky;
  top: 80px;
  flex-shrink: 0;
  align-self: flex-start;
  min-height: calc(100vh - 100px);
}

.column-separator:hover {
  background: linear-gradient(to right, transparent, #007bff 50%, transparent);
  border-left: 2px solid #0056b3;
  border-right: 2px solid #0056b3;
  width: 12px;
  box-shadow: 0 0 10px rgba(0, 123, 255, 0.3);
}

.column-separator:active {
  background: linear-gradient(to right, transparent, #0056b3 50%, transparent);
  border-left: 2px solid #003d82;
  border-right: 2px solid #003d82;
}

.separator-handle {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 16px;
  padding: 8px 2px;
  background-color: rgba(255, 255, 255, 0.8);
  border-radius: 4px;
  transition: all 0.3s ease;
  position: sticky;
  top: 50%;
  transform: translateY(-50%);
  height: 60px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.column-separator:hover .separator-handle {
  color: #ffffff;
  background-color: #007bff;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.4);
  transform: translateY(-50%) scale(1.1);
}

.column-separator:active .separator-handle {
  color: #ffffff;
  background-color: #0056b3;
  box-shadow: 0 2px 8px rgba(0, 86, 179, 0.5);
}

.items-column .itemContainer {
  /* Chaque item occupe toute la largeur de sa colonne */
  width: 100%;
  margin-bottom: var(--question-spacing, 1rem);
}

/* Default itemContainer spacing */
.itemContainer {
  margin-bottom: var(--question-spacing, 1rem);
}

/* 🔧 Fonctionnalité sticky pour le stimulus */
.stimulus-sticky {
  position: fixed !important;
  top: 20px;
  z-index: 100;
  background: rgba(248, 249, 250, 0.95);
  backdrop-filter: blur(5px);
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

/* Ajustements pour thème sombre */
.theme-dark .stimulus-sticky {
  background: rgba(52, 73, 94, 0.95);
  color: #ecf0f1;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
}

/* Styles spécifiques pour les positions fixed */
.stimulus-top.stimulus-sticky {
  /* Stimulus en haut qui devient fixed */
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  width: 90%;
  max-width: 800px;
}

.stimulus-bottom.stimulus-sticky {
  /* Stimulus en bas qui devient fixed */
  bottom: 20px;
  top: auto;
  left: 50%;
  transform: translateX(-50%);
  width: 90%;
  max-width: 800px;
}

.stimulus-left.stimulus-sticky {
  /* Stimulus à gauche fixed */
  top: 20px;
  left: 20px;
  width: 35%;
  max-height: calc(100vh - 120px);
  overflow-y: auto;
  margin-top: 250px;
}

.stimulus-right.stimulus-sticky {
  /* Stimulus à droite fixed */
  top: 20px;
  right: 20px;
  width: 35%;
  max-height: calc(100vh - 120px);
  overflow-y: auto;
  margin-top: 250px;
}

/* Amélioration visuelle lors du scroll */
.stimulus-sticky:hover {
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
  transform: translateY(-1px);
}

.theme-dark .stimulus-sticky:hover {
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.4);
}

/* 📹 Contrôle de la taille des vidéos dans le stimulus */
.stimulus-container iframe,
.stimulus-container video {
  max-width: 100% !important;
  height: auto !important;
  min-height: 200px !important;
  max-height: 400px !important;
  width: 100% !important;
  border-radius: 8px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
  margin: 10px 0 !important;
}

/* Styles spécifiques pour les iframes YouTube/Vimeo dans le stimulus */
.stimulus-container iframe[src*="youtube.com"],
.stimulus-container iframe[src*="youtu.be"],
.stimulus-container iframe[src*="vimeo.com"] {
  aspect-ratio: 16/9 !important;
  height: auto !important;
  min-height: unset !important;
  max-height: unset !important;
}

/* Styles pour différentes tailles prédéfinies */
.stimulus-container .video-small iframe,
.stimulus-container .video-small video {
  max-width: 400px !important;
  max-height: 225px !important;
}

.stimulus-container .video-medium iframe,
.stimulus-container .video-medium video {
  max-width: 600px !important;
  max-height: 338px !important;
}

.stimulus-container .video-large iframe,
.stimulus-container .video-large video {
  max-width: 800px !important;
  max-height: 450px !important;
}

.bigTitle{
  margin-left: 30px;
}
</style>
